import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button, Drawer } from '@bb/ui';
import type { AdminApi, SegmentRevisionView } from '../../api/client.ts';
import { formatWhen } from './SegmentsListPage.tsx';

interface Props {
  environmentId: string;
  segmentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Loads a revision into the editor; saving it makes a new revision. */
  onRestore?: (revision: SegmentRevisionView) => void;
}

const MARK = { added: '+', removed: '−', same: ' ' } as const;

/** Revisions of a segment (BB-46): who saved what and when, with the sentences each revision added or removed. */
export function SegmentRevisionsDrawer({ environmentId, segmentId, open, onOpenChange, onRestore }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const revisions = useQuery({ queryKey: ['segment-revisions', environmentId, segmentId], queryFn: () => api.segmentRevisions(environmentId, segmentId, 'en'), enabled: open });
  return (
    <Drawer open={open} onOpenChange={onOpenChange} title="Revisions" description="Every saved version of this segment, newest first.">
      {revisions.isPending ? (
        <p aria-busy="true">Loading revisions…</p>
      ) : revisions.isError ? (
        <p role="alert">Revisions could not be loaded.</p>
      ) : (
        <ol className="segment-revisions" aria-label="Revisions">
          {revisions.data.items.map((r, i) => (
            <li key={r.revision} aria-label={`Revision ${r.revision}`}>
              <h4>
                Revision {r.revision} · {formatWhen(r.createdAt)}
                {r.createdBy?.email ? ` · ${r.createdBy.email}` : ''}
              </h4>
              {(i === revisions.data.items.length - 1 || r.name !== revisions.data.items[i + 1]?.name) && <p>Name: {r.name}</p>}
              <ul aria-label={`Changes in revision ${r.revision}`}>
                {r.changes
                  .filter((c) => c.kind !== 'same')
                  .map((c, j) => (
                    <li key={`${c.kind}-${j}`} className={`diff diff--${c.kind}`}>
                      <span aria-hidden="true">{MARK[c.kind]} </span>
                      <span className="bb-visually-hidden">{c.kind === 'added' ? 'Added: ' : 'Removed: '}</span>
                      {c.sentence}
                    </li>
                  ))}
                {r.changes.every((c) => c.kind === 'same') && <li className="hint">The audience did not change.</li>}
              </ul>
              {onRestore && i > 0 && (
                <Button size="sm" variant="ghost" onClick={() => onRestore(r)}>
                  Restore revision {r.revision}
                </Button>
              )}
            </li>
          ))}
        </ol>
      )}
    </Drawer>
  );
}
